import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { stcBinService } from '../../../../../../services/stcBinService';
import { stcZoneService } from '../../../../../../services/stcZoneService';
import { stcBinTypeService } from '../../../../../../services/stcBinTypeService';
import { stcBinSizeService } from '../../../../../../services/stcBinSizeService';
import { stcBinGrpService } from '../../../../../../services/stcBinGrpService';
import { stcBinCategoryService } from '../../../../../../services/stcBinCategoryService';
import { stcResourceService } from '../../../../../../services/stcResourceService';
import { stcPriorityService } from '../../../../../../services/stcPriorityService';
import { StcBinDto } from '../../../../../../types/stcBin';
import { EnrichedBin, MasterDataLookups } from './types';
import { BulkUpdateModal } from '../../../rack-builder/BulkUpdateModal';
import { WarehouseTable } from '../../../../../shared/table';
import { createColumnFactory } from '../../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction, SummaryCalculation } from '../../../../../shared/table/types/table';

interface LocationsDataTableProps {
  warehouseID?: string;
}

export const LocationsDataTable: React.FC<LocationsDataTableProps> = ({ warehouseID }) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [bins, setBins] = useState<StcBinDto[]>([]);
  const [enrichedBins, setEnrichedBins] = useState<EnrichedBin[]>([]);
  const [masterData, setMasterData] = useState<MasterDataLookups | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBinCodes, setSelectedBinCodes] = useState<string[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Load all bins and master data on mount
  useEffect(() => {
    loadData();
  }, [companyNo, warehouseID]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch bins and all related master data in parallel
      const [
        binsData,
        zonesData,
        binTypesData,
        binSizesData,
        binGroupsData,
        binCategoriesData,
        resourcesData,
        prioritiesData
      ] = await Promise.all([
        warehouseID
          ? stcBinService.getByCompanyAndWarehouse(companyNo, warehouseID)
          : stcBinService.getCurrent(),
        stcZoneService.getCurrent(),
        stcBinTypeService.getCurrent(),
        stcBinSizeService.getCurrent(),
        stcBinGrpService.getCurrent(),
        stcBinCategoryService.getCurrent(),
        stcResourceService.getCurrent(),
        stcPriorityService.getCurrent()
      ]);

      // Create lookup maps for O(1) foreign key resolution
      const lookups: MasterDataLookups = {
        zones: new Map(zonesData.map(z => [z.stcZoneID, z])),
        binTypes: new Map(binTypesData.map(t => [t.binTypeNo, t])),
        binSizes: new Map(binSizesData.map(s => [s.binSizeNo, s])),
        binGroups: new Map(binGroupsData.map(g => [g.binGrpNo, g])),
        binCategories: new Map(binCategoriesData.map(c => [c.stcBinCatNo, c])),
        resources: new Map(resourcesData.map(r => [r.resourceNo, r])),
        priorities: new Map(prioritiesData.map(p => [p.priorityNo, p]))
      };

      // Enrich bins with related entity descriptions
      const enriched: EnrichedBin[] = binsData.map(bin => ({
        ...bin,
        zoneName: bin.stcZoneID ? lookups.zones.get(bin.stcZoneID)?.description1 : undefined,
        binTypeName: bin.binTypeNo ? lookups.binTypes.get(bin.binTypeNo)?.description1 : undefined,
        binSizeName: bin.binSizeNo ? lookups.binSizes.get(bin.binSizeNo)?.description1 : undefined,
        binGrpName: bin.binGrpNo ? lookups.binGroups.get(bin.binGrpNo)?.description1 : undefined,
        binCategoryName: bin.stcBinCatNo ? lookups.binCategories.get(bin.stcBinCatNo)?.lng_1 : undefined,
        resourceName: bin.resourceNo ? lookups.resources.get(bin.resourceNo)?.description1 : undefined,
        pickPriorityName: bin.pickPriorityNo ? lookups.priorities.get(bin.pickPriorityNo)?.description1 : undefined,
        putAwayPriorityName: bin.putAwayPriorityNo ? lookups.priorities.get(bin.putAwayPriorityNo)?.description1 : undefined
      }));

      setBins(binsData);
      setEnrichedBins(enriched);
      setMasterData(lookups);

    } catch (err) {
      console.error('Error loading locations data:', err);
      setError('Kunne ikke laste lokasjonsdata. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, warehouseID]);

  // Create column factory for type-safe columns
  const columnFactory = createColumnFactory<EnrichedBin>();

  // Handle bulk edit button
  const handleBulkEdit = useCallback(() => {
    if (selectedBinCodes.length === 0) {
      alert('Velg minst én lokasjon for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedBinCodes.length]);

  // Handle create new location
  const handleCreateNew = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  // Handle modal update success
  const handleUpdateSuccess = async () => {
    await loadData();
    setSelectedBinCodes([]);
    setShowBulkEditModal(false);
  };

  // Handle modal delete success
  const handleDeleteSuccess = async () => {
    await loadData();
    setSelectedBinCodes([]);
    setShowBulkEditModal(false);
  };

  // Column definitions using the factory
  const columns: ColumnDefinition<EnrichedBin>[] = useMemo(() => [
    columnFactory.createTextColumn('warehouseID', 'warehouseID', 'Lagersted', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createTextColumn('stcZoneID', 'stcZoneID', 'Sone', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.zoneName || String(value || '-')
    }),
    
    columnFactory.createTextColumn('binCode', 'binCode', 'Lokasjon', {
      minWidth: 120,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      minWidth: 200,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createNumberColumn('binGrpNo', 'binGrpNo', 'Lok.Grp', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binGrpName || String(value || '-')
    }),
    
    columnFactory.createNumberColumn('binTypeNo', 'binTypeNo', 'Type', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binTypeName || String(value || '-')
    }),
    
    columnFactory.createNumberColumn('binSizeNo', 'binSizeNo', 'Størrelse', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binSizeName || String(value || '-')
    }),
    
    columnFactory.createNumberColumn('stcBinCatNo', 'stcBinCatNo', 'Lok.Kategori', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.binCategoryName || String(value || '-')
    }),
    
    columnFactory.createNumberColumn('movementTypeNo', 'movementTypeNo', 'Bevegelseskode', {
      minWidth: 130,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createNumberColumn('resourceNo', 'resourceNo', 'Ressurs', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.resourceName || String(value || '-')
    }),
    
    columnFactory.createBooleanColumn('reservationAllowed', 'reservationAllowed', 'Reserverbar', {
      minWidth: 110,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createBooleanColumn('autoReplenishment', 'autoReplenishment', 'Automatisk påfylling', {
      minWidth: 160,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createBooleanColumn('pickableYesNo', 'pickableYesNo', 'Plukklokasjon', {
      minWidth: 130,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createBooleanColumn('negativeReservationAllowed', 'negativeReservationAllowed', 'Negativ beholdning gyldig', {
      minWidth: 190,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createDateColumn('lastCountedDate', 'lastCountedDate', 'Siste telledato', {
      minWidth: 140,
      sortable: true,
      filterType: 'none'
    }),
    
    columnFactory.createDateColumn('lastMovementDate', 'lastMovementDate', 'Siste transaksjonsdato', {
      minWidth: 180,
      sortable: true,
      filterType: 'none'
    }),
    
    columnFactory.createNumberColumn('pickPriorityNo', 'pickPriorityNo', 'Prioritet, plukk', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.pickPriorityName || String(value || '-')
    }),
    
    columnFactory.createNumberColumn('putAwayPriorityNo', 'putAwayPriorityNo', 'Prioritet, utlegg', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBin) => item.putAwayPriorityName || String(value || '-')
    }),
    
    columnFactory.createNumberColumn('customSortID', 'customSortID', 'Sortering', {
      minWidth: 100,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createTextColumn('pickCustomOrder', 'pickCustomOrder', 'Plukkrekkefølge', {
      minWidth: 140,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createNumberColumn('replenishMinLevel', 'replenishMinLevel', 'Påfylling min %', {
      minWidth: 130,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createNumberColumn('replenishMaxLevel', 'replenishMaxLevel', 'Påfylling maks %', {
      minWidth: 140,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
      minWidth: 120,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createBooleanColumn('sharedBinYesNo', 'sharedBinYesNo', 'FellesLokasjon', {
      minWidth: 130,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createNumberColumn('sharedBinMaxQuantity', 'sharedBinMaxQuantity', 'Maks ant. på felleslok.', {
      minWidth: 180,
      sortable: true,
      filterType: 'both'
    }),
    
    columnFactory.createBooleanColumn('sharedBinPreferEmptyBin', 'sharedBinPreferEmptyBin', 'Prioriter tomme felleslok.', {
      minWidth: 200,
      sortable: true,
      filterType: 'both'
    })
  ], []);

  // Table actions for bulk operations
  const tableActions: TableAction<EnrichedBin>[] = useMemo(() => [
    {
      id: 'bulk-edit',
      label: `Vedlikehold (${selectedBinCodes.length})`,
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: (data: EnrichedBin[], selected?: EnrichedBin[]) => selectedBinCodes.length === 0,
      variant: 'primary'
    }
  ], [selectedBinCodes.length, handleBulkEdit]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={enrichedBins}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'locations-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedBinCodes}
          onSelectionChange={(selectedIds) => {
            setSelectedBinCodes(selectedIds.map(id => String(id)));
          }}
          getRowId={(item) => item.binCode}
          tableActions={tableActions}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && selectedBinCodes.length > 0 && (() => {
        const selectedWarehouseID = bins.find(b => b.binCode === selectedBinCodes[0])?.warehouseID || '';

        return (
          <BulkUpdateModal
            isOpen={showBulkEditModal}
            onClose={() => setShowBulkEditModal(false)}
            selectedBinCodes={selectedBinCodes}
            warehouseID={selectedWarehouseID}
            filterOptions={null}
            onUpdateSuccess={handleUpdateSuccess}
            onDeleteSuccess={handleDeleteSuccess}
          />
        );
      })()}
    </div>
  );
};
